import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCallback, useMemo } from 'react';
import { fetchAccountsV2Promise } from 'reducers/accountsReducer';
import { getAccountLinksForSelectedUser } from 'reducers/usersReducer';
import { GetAccountsV2Response } from 'ts/api/accounts_v2';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import { ServerSideFilter } from 'utils/agGrid/types';
import { CLEARING } from 'constants/accountTypes';
import BalanceGridRow from '../types';
import { AccountLink } from 'ts/models/User.model';
import Member from 'ts/models/Member.model';
import { getSelectedMember } from 'reducers/membersReducer';

const useDatasource = ({ isUpdate }: { isUpdate: boolean }) => {
  const dispatch = useAppDispatch();


  const selectedMember: Member = useAppSelector(getSelectedMember);

  const userAccounts = useMemo(() => {
    if (!isUpdate) {
      return null;
    }

    return selectedMember?.ledgerAccounts || [];
  }, [isUpdate, selectedMember?.ledgerAccounts]);

  const getAccounts = useCallback(
    async (req: IServerSideGetRowsRequest) => {
      const body = parseAGRequest(req, { transformAttrs: true });
      let filter: ServerSideFilter[] = [];

       if (userAccounts) {
         filter = filter.concat([
           { attr: 'account_id', value: userAccounts, op: 'eq' },
         ]);
       }

      const result: GetAccountsV2Response = await fetchAccountsV2Promise(
        {
          ...body,
          filter: [...filter, ...(body?.filter ? body.filter : [])],
          showUsers: true,
        },
        dispatch,
      );
      return result;
    },
    [dispatch, userAccounts],
  );

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async ({
        request,
        success,
      }: IServerSideGetRowsParams<BalanceGridRow>) => {
        const accounts = await getAccounts(request);
        success({
          rowData: accounts.accounts ?? [],
          rowCount: accounts.count ?? undefined,
        });
      },
    }),
    [getAccounts],
  );
  return datasource;
};

export default useDatasource;
